import React from 'react';

import {createAnalysis} from 'analysis-tool/AnalysisActions';
import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import * as Analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import {ActionOrigin, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {StudySearchItem} from 'studies/StudySearchInput';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import CreateAnalysisForm from './CreateAnalysisForm';
import {SeedDataConfig, SeedDataConfigType} from './SeedDataTypes';
import useCreateAnalysisState from './useCreateAnalysisState';
export interface CreateAnalysisModalProps {
  onCreateAnalysis: (
    data: CreateAnalysisParams,
    /** Only relevant when `viaExistingTableProps` are present; will be an empty string otherwise */
    dataFrameName: string
  ) => Promise<unknown>;
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  actionOrigin?: ActionOrigin;
  /** Props to include when the analysis is being created via a dataframe. `initialDatasetName` will be used as the default dataFrame name in the modal */
  viaExistingTableProps?: {initialDatasetName: string};
  /**
   * The initial analysis name to use, if provided. If a function is provided, it will be called to get the initial analysis name.
   * This is useful for cases when the name contains something like a timestamp, and you want the name to be
   * dynamically generated when the modal is opened.
   */
  initialAnalysisName?: string | (() => string);
  title?: string;
  headerInlineBanner?: JSX.Element;
  /** Optional, initial seed data config corresponding to a SeedDataConfigType */
  initialSeedDataConfig?: SeedDataConfig;
  /** Note: has no effect when `showStudiesField` is false. */
  disableEditStudies?: boolean;
  initialStudySearchItems?: Array<StudySearchItem>;
  hideDataSharingWarningBanner?: boolean;
  /** Whether to show the Studies field */
  showStudiesField?: boolean;
}

export function CreateAnalysisModal(props: CreateAnalysisModalProps): JSX.Element {
  const {createAnalysisState, setCreateAnalysisState, requestParamsInfo} = useCreateAnalysisState({
    initialAnalysisName: props.initialAnalysisName,
    initialAnalysisTableName: props.viaExistingTableProps?.initialDatasetName,
    initialSeedDataConfig: props.initialSeedDataConfig,
    initialStudyItems: props.initialStudySearchItems,
  });

  const analyticsMetadata: AnalyticsMetadataForContext = {
    actionOrigin: props.actionOrigin ?? ActionOrigin.ANALYSIS_TOOL,
    modalType: 'create_analysis',
  };
  const analyticsModule = ANALYTICS_MODULE.ANALYSIS_TOOL;

  async function handleRequestSubmit(): Promise<void> {
    assert(requestParamsInfo.isComplete, 'Expected create analysis request params to be complete');
    FolderDefaults.set(FolderDefaultsKey.ANALYSIS, requestParamsInfo.requestParams.folderId);
    await withLoadingScreen(
      props.onCreateAnalysis(requestParamsInfo.requestParams, requestParamsInfo.analysisTableName ?? ''),
      'Creating analysis...'
    );
    props.onRequestSubmit();
  }

  useOnMount(() => {
    Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, analyticsModule, analyticsMetadata);
  });

  return (
    <Modal
      fullWidth={createAnalysisState.seedDataConfig?.type === SeedDataConfigType.WORKSHEET}
      large={createAnalysisState.seedDataConfig?.type === SeedDataConfigType.MULTIPLE_STUDIES}
      title={props.title ?? 'Create analysis'}
      onRequestSubmit={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
          analyticsModule,
          analyticsMetadata
        );
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.SUBMITTED_CREATE_ANALYSIS,
          analyticsModule,
          analyticsMetadata
        );
        handleRequestSubmit();
      }}
      onRequestClose={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
          analyticsModule,
          analyticsMetadata
        );
        props.onRequestClose();
      }}
      buttons={[
        {
          submit: true,
          text: 'Create',
          style: ButtonStyle.SECONDARY,
          disabled: !requestParamsInfo.isComplete,
        },
      ]}
    >
      <CreateAnalysisForm
        createAnalysisState={createAnalysisState}
        setCreateAnalysisState={setCreateAnalysisState}
        disableEditStudies={props.disableEditStudies}
        headerInlineBanner={props.headerInlineBanner}
        hideDataSharingWarningBanner={props.hideDataSharingWarningBanner}
        showStudiesField={props.showStudiesField}
      />
    </Modal>
  );
}

export default function ConnectedCreateAnalysisModal(
  props: Omit<CreateAnalysisModalProps, 'onCreateAnalysis'>
): JSX.Element {
  return <CreateAnalysisModal {...props} onCreateAnalysis={createAnalysis} />;
}
